/* eslint-disable no-param-reassign,func-names */
import { renderIntoSharedReactRoot } from '~/common/SharedReactRootManager'
import { isClient } from '~/common/nextJsUtils'
import { componentAcceptsRef } from '~/common/reactUtils'

/**
 * A way to render React components with props easily with jQuery
 *
 * ## Register the React Component
 *
 * In your React Component file, register the component with jQuery using `asJqueryPlugin`
 * ```
 * const Greeting = ({ person }) => <div>Hello {person}</div>
 * asJqueryPlugin('Greeting', Greeting, { person: "Bob" })
 * ```
 *
 * ## Rendering, Selecting and Updating Props with jQuery
 *
 * Select an element and render using the `react` function
 * ```
 * $('#greeting').react('Greeting', { person: 'Frank' })
 * ```
 *
 * ## Auto-Render the React Component in your jQuery App
 * ```
 * <div data-react="Greeting" data-react-auto></div>
 * ```
 * This will render Greeting into that div on page load. (Make sure the react bundle is loaded after the jquery.js and html)
 *
 * Use the top-level `$.react` function to select elements by their `data-react` attribute. Optionally pass some props.
 *
 * ```
 * $.react('Greeting') // Selects all <div data-react="Greeting"></div>
 * $.react('Greeting', { person: "Frank" }) // Selects and sets props
 * ```
 */

const registry = {}

// This is how React components register themselves as available within jQuery
export default function asJqueryPlugin(componentName, Component, defaults = {}) {
  registry[componentName] = { Component, defaults }
}

function installJqueryReactPlugin($) {
  function render($els, updateProps = false, ...args) {
    let componentName
    let props = {}
    let opts = {}

    if (args.length === 1) {
      props = args[0]
    }
    if (args.length === 2) {
      componentName = args[0]
      props = args[1]
    }
    if (args.length === 3) {
      componentName = args[0]
      props = args[1]
      opts = args[2]
    }
    $els.each(function render() {
      const name = componentName || $(this).data('react')
      const entry = registry[name]
      if (!entry) throw Error(`${componentName} component is not registered.`)
      const { Component, defaults } = entry
      if (updateProps) {
        // If the cmp is already mounted, update the props using the existing cmp instance method
        const cmp = $(this).data('cmp')
        if (cmp) {
          cmp.update(props) // This comes from the renderIntoSharedReactRoot util
          return
        }
      }
      const refProp = componentAcceptsRef(Component) ? { ref: this } : {}
      const cmp = renderIntoSharedReactRoot(<Component {...refProp} key={props.key} {...defaults} {...props} />, this, {
        nodeName: name,
        ...opts
      })
      $(this).data('cmp', cmp)
    })
    return $els
  }

  // Add the plugin function jQuery
  // Use this to render with a full props object
  $.fn.react = function renderReactIntoElements(...args) {
    return render(this, false, ...args)
  }

  // Use this to only make partial prop updates that merge with existing props
  $.fn.updateReactProps = function updateReactProps(...args) {
    return render(this, true, ...args)
  }

  $.react = function getReactElementsByName(name, props) {
    const $els = $(`[data-react=${name}]`)
    if (props) {
      $els.react(props)
    }
    return $els
  }

  $.updateReactProps = function getReactElementsByName(name, props) {
    const $els = $(`[data-react=${name}]`)
    if (props) {
      $els.updateReactProps(props)
    }
    return $els
  }

  // Auto-activate any elements with data-react attrs
  $(() => {
    $('[data-react-auto]').each(function renderReactOnLoad() {
      $(this).react()
    })
  })
}

if (isClient()) {
  const jQueryIsDefined = typeof window.$ !== 'undefined'
  if (jQueryIsDefined) installJqueryReactPlugin(window.$)
}
